import { energyAnalysisApiSlice as api } from '../../../apiSlice';
export const addTagTypes = ['Energy Savings'] as const;
const injectedRtkApi = api
  .enhanceEndpoints({
    addTagTypes,
  })
  .injectEndpoints({
    endpoints: (build) => ({
      cachedEnergySavingsEnergySavingsAccountIdEnergyGroupIdGet: build.query<
        CachedEnergySavingsEnergySavingsAccountIdEnergyGroupIdGetApiResponse,
        CachedEnergySavingsEnergySavingsAccountIdEnergyGroupIdGetApiArg
      >({
        query: (queryArg) => ({
          url: `/energy_savings/${queryArg.accountId}/${queryArg.energyGroupId}`,
        }),
        providesTags: ['Energy Savings'],
      }),
    }),
    overrideExisting: false,
  });
export { injectedRtkApi as energyAnalysisOpenApi };
export type CachedEnergySavingsEnergySavingsAccountIdEnergyGroupIdGetApiResponse =
  /** status 200 Successful Response */ CachedEnergySavingsOutput;
export type CachedEnergySavingsEnergySavingsAccountIdEnergyGroupIdGetApiArg = {
  /** The energy group id to get the energy overview for. */
  energyGroupId: number;
  /** The account id to get the energy overview for. */
  accountId: number;
};
export type EnergyDataPoint = {
  /** The timestamp of the data point. Format: YYYY-MM-DDTHH:MM:SSZ */
  timestamp: string;
  /** The value of the data point in kWh. */
  value: number | null;
};
export type EnergySavingsModel = {
  /** The actual energy usage in kWh. */
  ActualEnergyUsage: number;
  /** The predicted energy usage based on the baseline data in kWh. */
  PredictedEnergyUsageFromBaseline: number;
  /** The average baseline energy usage in kWh. */
  AverageBaselineEnergyUsage: number;
  /** The average comparison energy usage in kWh. */
  AverageComparisonEnergyUsage: number;
  /** The energy savings in percentage. */
  EnergySavingsPercentage: number;
  /** The energy savings in kWh. */
  EnergySavings: number;
  /** The base unit for the energy savings. */
  BaseUnit?: string;
};
export type BasicEnergySavingsModel = {
  /** The average baseline energy usage in kWh. */
  AverageBaselineEnergyUsage: number;
  /** The average comparison energy usage in kWh. */
  AverageComparisonEnergyUsage: number;
  /** The energy savings in percentage. */
  EnergySavingsPercentage: number;
  /** The energy savings in kWh. */
  EnergySavings: number;
  /** The base unit for the energy savings. */
  BaseUnit?: string;
};
export type Co2ESavingsModel = {
  /** The actual CO2e emissions in g. */
  ActualCO2eEmission: number;
  /** The predicted CO2e emissions based on the baseline data in g. */
  PredictedCO2eEmission: number;
  /** The CO2e savings in percentage. */
  CO2eSavingsPercentage: number;
  /** The CO2e savings in g. */
  CO2eSavings: number;
  /** The base unit for the CO2e savings. */
  BaseUnit?: string;
};
export type ModelMetadata = {
  /** List of all features used in the final model. */
  features_used: string[];
  /** List of calendar features used (e.g., hour, dayofweek, week). */
  calendar_features_used: string[];
  /** List of weather features used (empty if weather not used). */
  weather_features_used?: string[];
  /** Whether weather data was provided to the algorithm. */
  weather_data_provided: boolean;
  /** Whether weather data improved model performance above the threshold. */
  weather_data_beneficial?: boolean;
  /** NRMSE improvement percentage from adding weather data. Positive value means weather improved the model. None if weather not tested. */
  weather_improvement_pct?: number | null;
  /** Feature name to importance score mapping (gain-based, normalized to sum to 1.0). */
  feature_importance?: {
    [key: string]: number;
  };
  /** NRMSE of the calendar-only model. */
  nrmse_calendar_only: number;
  /** NRMSE of the model with weather features (None if weather not tested). */
  nrmse_with_weather?: number | null;
  /** Type of model used: 'calendar_only' or 'calendar_and_weather'. */
  model_type: string;
  /** Weather features removed due to low importance during feature reduction. */
  weather_features_removed?: string[];
};
export type ApiWarning = {
  /** The warning message */
  message: string;
  /** The warning type */
  warning_type: string;
  /** The warning details */
  details?: object;
};
export type EnergySavingsOutput = {
  /** The baseline energy usage. */
  BaselineEnergyUsage: EnergyDataPoint[];
  /** The comparison energy usage. */
  ComparisonEnergyUsage: EnergyDataPoint[];
  /** The predicted energy usage based on the baseline data. */
  PredictedEnergyUsage: EnergyDataPoint[];
  /** The initiative energy usage, that is not part of the baseline nor included in the comparison period because it is not part of the energy savings calculation. */
  InitiativeEnergyUsage: EnergyDataPoint[];
  /** The energy savings summary. */
  EnergySavingsSummary: EnergySavingsModel | BasicEnergySavingsModel | null;
  /** The CO2e savings summary. */
  CO2eSavingsSummary?: Co2ESavingsModel | null;
  /** The normalized root mean square error of the advanced model. */
  AdvancedModelNRMSE?: number | null;
  /** Metadata about the model used for prediction, including features used, weather data decision, and feature importance. None for basic algorithm. */
  AdvancedModelMetadata?: ModelMetadata | null;
  Warnings?: ApiWarning[];
};
export type TimestampsSummaryModel = {
  /** The minimum earliest timestamp of the data across all nodes. Format: YYYY-MM-DDTHH:MM:SSZ */
  minEarliestDataTimestamp: string | null;
  /** The maximum earliest timestamp of the data across all nodes. Format: YYYY-MM-DDTHH:MM:SSZ */
  maxEarliestDataTimestamp: string | null;
  /** The minimum latest timestamp of the data across all nodes. Format: YYYY-MM-DDTHH:MM:SSZ */
  minLatestDataTimestamp: string | null;
  /** The maximum latest timestamp of the data across all nodes. Format: YYYY-MM-DDTHH:MM:SSZ */
  maxLatestDataTimestamp: string | null;
};
export type InitiativeForecastSavings = {
  /** The estimated total savings since initiative start. */
  BasicEstimatedTotalSavings?: number | null;
  /** The estimated yearly savings. */
  BasicEstimatedYearlySavings?: number | null;
  /** The estimated total CO2e savings since initiative start. */
  BasicEstimatedTotalSavingsCO2e?: number | null;
  /** The estimated yearly CO2e savings. */
  BasicEstimatedYearlySavingsCO2e?: number | null;
  /** The percentage savings compared to the baseline. */
  BasicPercentageSavings?: number | null;
  /** The percentage CO2e savings compared to the baseline. */
  BasicPercentageSavingsCO2e?: number | null;
  /** The estimated yearly savings using the advanced model. */
  AdvEstimatedYearlySavings?: number | null;
  /** The estimated yearly CO2e savings using the advanced model. */
  AdvEstimatedYearlySavingsCO2e?: number | null;
  /** The estimated total savings since initiative start using the advanced model. */
  AdvEstimatedTotalSavings?: number | null;
  /** The estimated total CO2e savings since initiative start using the advanced model. */
  AdvEstimatedTotalSavingsCO2e?: number | null;
  /** The percentage savings compared to the baseline using the advanced model. */
  AdvPercentageSavings?: number | null;
  /** The percentage CO2e savings compared to the baseline using the advanced model. */
  AdvPercentageSavingsCO2e?: number | null;
};
export type CachedInitiativeOutput = {
  /** The id of the cached initiative. */
  Id: number;
  /** The name of the cached initiative. */
  Name: string;
  /** The state of the cached initiative. */
  State: string;
  /** The energy savings output for the basic energy savings. */
  BasicEnergySavings: EnergySavingsOutput | null;
  /** The energy savings output for the advanced energy savings. */
  AdvancedEnergySavings: EnergySavingsOutput | null;
  /** If the advanced model was used for the energy savings calculation. */
  AdvancedModelUsed: boolean;
  /** If the energy savings calculation was successful. */
  Success: boolean;
  /** Gives information about in what ranges the data is present. */
  TimestampsSummary: TimestampsSummaryModel;
  /** The forecasted energy savings for the initiative. Estimated yearly savings and estimated total savings since initiative start. */
  ForecastedSavings?: InitiativeForecastSavings | null;
};
export type CachedEnergySavingsOutput = {
  /** The id of the energy group. */
  Id: number;
  /** The name of the energy group. */
  Name: string;
  /** The cached energy savings output for the each initiative in the energy group. */
  InitiativeEnergySavings: CachedInitiativeOutput[];
  /** Warnings generated while loading initiative cache data. */
  Warnings?: ApiWarning[];
};
export type ValidationError = {
  loc: (string | number)[];
  msg: string;
  type: string;
};
export type HttpValidationError = {
  detail?: ValidationError[];
};
export const {
  useCachedEnergySavingsEnergySavingsAccountIdEnergyGroupIdGetQuery,
} = injectedRtkApi;
